import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

export interface NILGoExportDeal {
  id: string;
  athleteName: string;
  sport: string;
  dealType: string;
  brandName: string;
  compensationAmount: number;
  startDate: string;
  endDate: string;
  vbpDescription: string;
  fmvJustification: string;
  institutionApprovalDate: string;
  complianceOfficer: string;
}

export async function generateNILGoExport(dealIds: string[]): Promise<string> {
  // Fetch deals with athlete info
  const { data: deals, error } = await supabase
    .from("deals")
    .select(`
      *,
      athletes (name, sport)
    `)
    .in("id", dealIds)
    .eq("status", "active");

  if (error) throw error;
  if (!deals || deals.length === 0) throw new Error("No deals found for export");

  // Get institution settings for compliance officer info
  const { data: settings } = await supabase
    .from("institution_settings")
    .select("compliance_officer_emails, institution_name")
    .single();

  const complianceOfficer = settings?.compliance_officer_emails?.[0] || "Compliance Office";
  const institutionName = settings?.institution_name || "Institution";

  // Generate CSV content
  const headers = [
    "Deal ID",
    "Athlete Name",
    "Sport",
    "Deal Type",
    "Brand Name",
    "Compensation Amount",
    "Start Date",
    "End Date",
    "Valid Business Purpose",
    "Fair Market Value Justification",
    "Institution Approval Date",
    "Compliance Officer",
    "Institution"
  ];

  const rows = deals.map((deal: any) => [
    deal.id,
    deal.athletes?.name || "Unknown",
    deal.athletes?.sport || "Unknown",
    deal.deal_type || "General",
    deal.brand_name,
    deal.amount,
    format(new Date(deal.start_date), "yyyy-MM-dd"),
    deal.end_date ? format(new Date(deal.end_date), "yyyy-MM-dd") : "Ongoing",
    deal.description || "Service-based endorsement",
    deal.ai_explanation || `FMV verified for ${deal.deal_type}`,
    deal.approved_at ? format(new Date(deal.approved_at), "yyyy-MM-dd") : "Pending",
    complianceOfficer,
    institutionName
  ]);

  // Convert to CSV
  const csvContent = [
    headers.join(","),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
  ].join("\n");

  // Generate batch ID
  const batchId = crypto.randomUUID();
  const fileName = `NIL_Go_Export_${format(new Date(), "yyyy-MM-dd_HHmmss")}.csv`;

  // Store submission record
  const { error: insertError } = await supabase
    .from("nil_go_submissions")
    .insert(
      dealIds.map(dealId => ({
        deal_id: dealId,
        athlete_id: deals.find(d => d.id === dealId)?.athlete_id,
        submission_deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        submitted: true,
        submitted_at: new Date().toISOString(),
        batch_id: batchId,
        export_file_path: fileName,
      }))
    );

  if (insertError) console.error("Error logging NIL Go submission:", insertError);

  // Create downloadable blob
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  
  // Trigger download
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);

  return fileName;
}

export async function validateDealsForNILGo(dealIds: string[]): Promise<{
  valid: boolean;
  errors: string[];
}> {
  const { data: deals, error } = await supabase
    .from("deals")
    .select("*")
    .in("id", dealIds);

  if (error) return { valid: false, errors: [error.message] };

  const errors: string[] = [];

  deals?.forEach((deal: any) => {
    if (deal.status !== "active") {
      errors.push(`Deal ${deal.title} must be approved (status: ${deal.status})`);
    }
    if (!deal.brand_name) {
      errors.push(`Deal ${deal.title} missing brand name`);
    }
    if (!deal.amount || deal.amount <= 0) {
      errors.push(`Deal ${deal.title} missing valid compensation amount`);
    }
    if (!deal.description) {
      errors.push(`Deal ${deal.title} missing VBP description`);
    }
    if (!deal.approved_at) {
      errors.push(`Deal ${deal.title} missing institution approval date`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}
